import { useState, useEffect } from "react";
import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo";
import { generateOrganizationSchema } from "@/components/seo/SchemaMarkup";
import SoldListingsGrid from "@/components/sold/SoldListingsGrid";
import { TrendingUp, DollarSign } from "lucide-react";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const Sold = () => {
  const [stats, setStats] = useState<{ total: number; volume: number } | null>(null);

  useEffect(() => {
    document.title = "Recently Sold Homes in South Jersey | Prime Realty Partners";
  }, []);

  const organizationSchema = generateOrganizationSchema();
  const soldListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Recently Sold Properties in South Jersey",
    description: "Properties sold by Prime Realty Partners in South Jersey",
  };

  return (
    <Layout>
      <SEOHead
        title="Recently Sold Homes in South Jersey | Prime Realty Partners"
        description="See recently sold homes in South Jersey. Browse completed sales in Cherry Hill, Moorestown, Haddonfield, and surrounding communities with Prime Realty Partners."
        canonicalUrl="https://www.prpsj.com/sold"
        keywords="sold homes South Jersey, recently sold properties NJ, closed real estate transactions Camden County, Burlington County sold homes, Prime Realty Partners sales"
        schema={[organizationSchema, soldListSchema]}
        breadcrumbs={[{ name: "Home", url: "https://www.prpsj.com" }, { name: "Sold", url: "https://www.prpsj.com/sold" }]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/10 via-background to-background py-16 md:py-24">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Recently Sold Properties
            </h1>
            <p className="text-xl text-muted-foreground mb-4">
              Browse recently sold homes across South Jersey. See completed sales and closing prices in Cherry Hill, Moorestown, Haddonfield, Mount Laurel, Voorhees, Medford, and surrounding communities. Our sold listings showcase the results Prime Realty Partners delivers for our clients.
            </p>

            {/* Stats */}
            {stats && stats.total > 0 && (
              <div className="flex flex-wrap justify-center gap-6">
                <div className="flex items-center gap-3 bg-card border border-border rounded-lg px-6 py-4 shadow-sm">
                  <TrendingUp className="h-6 w-6 text-primary" />
                  <div className="text-left">
                    <p className="text-2xl font-bold text-foreground">{stats.total.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">Properties Sold</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-card border border-border rounded-lg px-6 py-4 shadow-sm">
                  <DollarSign className="h-6 w-6 text-primary" />
                  <div className="text-left">
                    <p className="text-2xl font-bold text-foreground">{formatCurrency(stats.volume)}</p>
                    <p className="text-sm text-muted-foreground">Volume Sold</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Sold Listings Grid */}
      <section className="py-10 md:py-14">
        <div className="container">
          <SoldListingsGrid showFilters showPagination onStatsLoaded={setStats} />
        </div>
      </section>
    </Layout>
  );
};

export default Sold;
